/**
 * AIWorkspace Component
 * Main workspace component for AI actions
 * 
 * FEATURES:
 * - Translate, Generate, Custom Prompt actions
 * - Horizontal scrollable cards with navigation arrows
 * - History section with localStorage persistence
 * - Collapsible history view
 * - ✅ FIX: Scroll vertical com header sticky
 * - ✅ FIX: Cards com altura dinâmica
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { AlertCircle, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, History } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getWorkspaceColors } from './styles';
import { MAX_REGENERATIONS } from './constants';
import ResultCard from './ResultCard';
import { TranslateAction, GenerateResponseAction, CustomPromptAction } from './actions';

// ============================================
// ScrollableCardRow Component (inline)
// ✅ FIX: align-items: flex-start para cards com altura dinâmica
// ============================================
const ScrollableCardRow = ({ children, workspaceColors }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
  }, []);

  useEffect(() => {
    checkScroll();
    const scrollEl = scrollRef.current;
    if (scrollEl) {
      scrollEl.addEventListener('scroll', checkScroll);
      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(scrollEl);
      return () => {
        scrollEl.removeEventListener('scroll', checkScroll);
        resizeObserver.disconnect();
      };
    }
  }, [children, checkScroll]);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 340;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const hasChildren = React.Children.count(children) > 0;
  if (!hasChildren) return null;

  const styles = {
    wrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'flex-start', // ✅ FIX: Era 'center', agora alinha no topo
    },
    scrollContainer: {
      display: 'flex',
      gap: 12,
      padding: '8px 40px',
      overflowX: 'auto',
      overflowY: 'hidden',
      flex: 1,
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      alignItems: 'flex-start', // ✅ FIX: Cards alinhados no topo
    },
    arrowBtn: {
      position: 'absolute',
      top: 100, // ✅ FIX: Posição fixa em vez de 50%
      transform: 'translateY(-50%)',
      width: 32,
      height: 32,
      borderRadius: '50%',
      border: 'none',
      backgroundColor: workspaceColors.accentPrimary,
      color: workspaceColors.bgPrimary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      zIndex: 10,
      transition: 'all 0.2s ease',
      opacity: 0.85,
    },
    arrowHidden: {
      opacity: 0,
      pointerEvents: 'none',
    },
  };

  return (
    <div style={styles.wrapper}>
      {/* Left Arrow */}
      <button
        style={{
          ...styles.arrowBtn,
          left: 4,
          ...(canScrollLeft ? {} : styles.arrowHidden),
        }}
        onClick={() => scroll('left')}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.85';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        <ChevronLeft size={18} />
      </button>

      {/* Scrollable Content */}
      <div ref={scrollRef} style={styles.scrollContainer} className="hide-scrollbar-workspace">
        {children}
      </div>

      {/* Right Arrow */}
      <button
        style={{
          ...styles.arrowBtn,
          right: 4,
          ...(canScrollRight ? {} : styles.arrowHidden),
        }}
        onClick={() => scroll('right')}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.85';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
};

// ============================================
// Main AIWorkspace Component
// ============================================
const AIWorkspace = ({
  // Workspace visibility
  isOpen,
  onToggleWorkspace,
  selectedCount,
  inputText = '',
  
  // Dropdown control
  activeDropdown,
  onToggleDropdown,
  onCloseDropdown,
  
  // Translate state
  translateLanguages,
  onToggleTranslateLanguage,
  translateResults,
  translateProcessing,
  translateError,
  hasTranslateResults,
  
  // Generate state (no longer needs language selection)
  generateResults,
  generateProcessing,
  generateError,
  hasGenerateResults,
  
  // Custom Prompt state
  customResults = [],
  customProcessing = false,
  customError = null,
  hasCustomResults = false,
  
  // History state
  historyItems = [],
  isHistoryOpen = false,
  onToggleHistory,
  onRemoveFromHistory,
  onClearHistory,
  hasHistory = false,
  
  // Callbacks
  onClear,
  onExecuteTranslate,
  onExecuteGenerate,
  onExecuteCustom,
  onUseResponse,
  onRegenerateTranslate,
  onRegenerateGenerate,
  onRegenerateCustom,
  onRemoveTranslateResult,
  onRemoveGenerateResult,
  onRemoveCustomResult,
  
  // Regeneration tracking
  getTranslateRemainingRegenerations,
  getGenerateRemainingRegenerations,
  getCustomRemainingRegenerations,
  regeneratingId = null,
}) => {
  const { colors, theme } = useTheme();
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');

  const hasAnyResults = hasTranslateResults || hasGenerateResults || hasCustomResults;
  const isTranslateDisabled = !inputText?.trim();
  const isGenerateDisabled = selectedCount === 0;

  // Calculate dynamic max height
  const getMaxHeight = () => {
    if (!isOpen) return '0px';
    
    // Quando histórico está aberto, usa altura relativa à viewport
    if (isHistoryOpen && hasHistory) {
      return hasAnyResults ? '70vh' : '70vh';
    }
    
    // Resultados atuais apenas
    if (hasAnyResults) return '70vh';
    
    // Apenas botões de ação
    return '70px';
  };

  const workspaceStyles = {
    toggleBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '8px 16px',
      backgroundColor: workspaceColors.bgTertiary,
      borderTop: `1px solid ${workspaceColors.borderColor}`,
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
      gap: 8,
    },
    toggleBarHandle: {
      width: 40,
      height: 4,
      borderRadius: 2,
      backgroundColor: workspaceColors.textTertiary,
      opacity: 0.5,
      transition: 'all 0.15s ease',
    },
    toggleBarText: {
      fontSize: 11,
      fontWeight: 600,
      color: workspaceColors.textTertiary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      transition: 'color 0.15s ease',
    },
    toggleBarIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: workspaceColors.textTertiary,
      transition: 'color 0.15s ease',
    },
    // Container principal
    container: {
      backgroundColor: workspaceColors.workspaceBg || workspaceColors.bgTertiary,
      overflow: 'hidden',
      transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      maxHeight: getMaxHeight(),
      display: 'flex',
      flexDirection: 'column',
    },
    // Área de ações (sempre fixa no topo)
    actionsRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '12px 16px',
      borderBottom: (hasAnyResults || (isHistoryOpen && hasHistory)) 
        ? `1px solid ${workspaceColors.borderColor}` 
        : 'none',
      flexShrink: 0,
    },
    // ✅ Área de conteúdo com scroll vertical
    contentArea: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      scrollbarWidth: 'thin',
      scrollbarColor: `${workspaceColors.textTertiary}40 transparent`,
    },
    divider: {
      width: 1,
      height: 28,
      backgroundColor: workspaceColors.borderColor,
    },
    historyBtn: {
      marginLeft: 'auto',
      padding: '6px 12px',
      borderRadius: 16,
      border: 'none',
      backgroundColor: isHistoryOpen 
        ? `${workspaceColors.accentPrimary}20` 
        : 'transparent',
      color: isHistoryOpen 
        ? workspaceColors.accentPrimary 
        : workspaceColors.textSecondary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      fontWeight: 500,
      transition: 'all 0.15s ease',
    },
    historyBadge: {
      minWidth: 18,
      height: 18,
      padding: '0 5px',
      borderRadius: 9,
      backgroundColor: workspaceColors.accentPrimary,
      color: 'white',
      fontSize: 10,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    selectedBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '6px 12px',
      backgroundColor: `${workspaceColors.accentPrimary}20`,
      color: workspaceColors.accentPrimary,
      borderRadius: 16,
      fontSize: 12,
      fontWeight: 600,
    },
    clearBtn: {
      padding: '2px 4px',
      border: 'none',
      background: 'transparent',
      color: workspaceColors.accentPrimary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      fontSize: 16,
      fontWeight: 400,
      marginLeft: 4,
      lineHeight: 1,
    },
    errorContainer: {
      padding: '10px 16px',
      backgroundColor: `${workspaceColors.danger}15`,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      color: workspaceColors.danger,
      fontSize: 13,
      flexShrink: 0,
    },
    // ✅ Header sticky DENTRO da contentArea
    historySectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 16px',
      backgroundColor: workspaceColors.workspaceBg || workspaceColors.bgTertiary,
      borderBottom: `1px solid ${workspaceColors.borderColor}`,
      position: 'sticky',
      top: 0,
      zIndex: 10,
    },
    historySectionTitle: {
      fontSize: 11,
      fontWeight: 600,
      color: workspaceColors.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      display: 'flex',
      alignItems: 'center',
      gap: 6,
    },
    clearHistoryBtn: {
      padding: '4px 8px',
      border: 'none',
      background: 'transparent',
      color: workspaceColors.danger,
      cursor: 'pointer',
      fontSize: 11,
      fontWeight: 500,
      borderRadius: 4,
      transition: 'background-color 0.15s ease',
    },
    // ✅ Histórico cards container
    historyCardsContainer: {
      borderBottom: hasAnyResults ? `1px solid ${workspaceColors.borderColor}` : 'none',
      paddingBottom: 8,
    },
    // Current results section
    currentSectionHeader: {
      padding: '12px 16px 4px',
      fontSize: 11,
      fontWeight: 600,
      color: workspaceColors.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      backgroundColor: workspaceColors.workspaceBg || workspaceColors.bgTertiary,
      position: 'sticky',
      top: isHistoryOpen && hasHistory ? 37 : 0,
      zIndex: 9,
    },
  };

  return (
    <>
      {/* Toggle Bar */}
      <div 
        style={workspaceStyles.toggleBar}
        onClick={onToggleWorkspace}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = workspaceColors.bgSecondary;
          const handle = e.currentTarget.querySelector('.toggle-handle');
          const text = e.currentTarget.querySelector('.toggle-text');
          const icon = e.currentTarget.querySelector('.toggle-icon');
          if (handle) handle.style.backgroundColor = workspaceColors.accentPrimary;
          if (text) text.style.color = workspaceColors.accentPrimary;
          if (icon) icon.style.color = workspaceColors.accentPrimary;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = workspaceColors.bgTertiary;
          const handle = e.currentTarget.querySelector('.toggle-handle');
          const text = e.currentTarget.querySelector('.toggle-text');
          const icon = e.currentTarget.querySelector('.toggle-icon');
          if (handle) {
            handle.style.backgroundColor = workspaceColors.textTertiary;
            handle.style.opacity = '0.5';
          }
          if (text) text.style.color = workspaceColors.textTertiary;
          if (icon) icon.style.color = workspaceColors.textTertiary;
        }}
      >
        <div className="toggle-handle" style={workspaceStyles.toggleBarHandle} />
        <span className="toggle-text" style={workspaceStyles.toggleBarText}>
          {isOpen ? 'Fechar' : 'Abrir'} AI Workspace
        </span>
        <div className="toggle-icon" style={workspaceStyles.toggleBarIcon}>
          {isOpen ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </div>
      </div>

      {/* Workspace Content */}
      <div style={workspaceStyles.container}>
        {/* Errors - Sempre no topo */}
        {translateError && (
          <div style={workspaceStyles.errorContainer}>
            <AlertCircle size={16} />
            <span>Tradução: {translateError}</span>
          </div>
        )}
        {generateError && (
          <div style={workspaceStyles.errorContainer}>
            <AlertCircle size={16} />
            <span>Geração: {generateError}</span>
          </div>
        )}
        {customError && (
          <div style={workspaceStyles.errorContainer}>
            <AlertCircle size={16} />
            <span>Prompt IA: {customError}</span>
          </div>
        )}

        {/* Actions Row - Sempre fixa */}
        <div style={workspaceStyles.actionsRow}>
          {/* Translate Action */}
          <TranslateAction
            isDropdownOpen={activeDropdown === 'translate'}
            selectedLanguages={translateLanguages}
            onToggleDropdown={() => onToggleDropdown('translate')}
            onToggleLanguage={onToggleTranslateLanguage}
            onCloseDropdown={onCloseDropdown}
            onExecute={onExecuteTranslate}
            isProcessing={translateProcessing}
            isDisabled={isTranslateDisabled}
            disabledReason="Digite algo para traduzir"
          />

          {/* Generate Response Action - Direct execution, no dropdown */}
          <GenerateResponseAction
            onExecute={onExecuteGenerate}
            isProcessing={generateProcessing}
            isDisabled={isGenerateDisabled}
            disabledReason="Selecione mensagens"
          />

          {/* Custom Prompt Action */}
          <CustomPromptAction
            onExecute={onExecuteCustom}
            isProcessing={customProcessing}
          />

          {/* Selected count badge */}
          {selectedCount > 0 && (
            <div style={workspaceStyles.selectedBadge}>
              {selectedCount} selecionada{selectedCount !== 1 ? 's' : ''}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClear?.();
                }}
                style={workspaceStyles.clearBtn}
                title="Limpar seleção"
              >
                ×
              </button>
            </div>
          )}

          {/* History Button */}
          {hasHistory && (
            <button
              style={workspaceStyles.historyBtn}
              onClick={(e) => {
                e.stopPropagation();
                onToggleHistory?.();
              }}
              onMouseEnter={(e) => {
                if (!isHistoryOpen) {
                  e.currentTarget.style.backgroundColor = workspaceColors.bgSecondary;
                }
              }}
              onMouseLeave={(e) => {
                if (!isHistoryOpen) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span>{isHistoryOpen ? "Fechar Histórico" : "Abrir Histórico"}</span>
            </button>
          )}
        </div>

        {/* ✅ Área de conteúdo com scroll vertical */}
        <div style={workspaceStyles.contentArea} className="workspace-content-scroll">
          {/* History Section */}
          {isHistoryOpen && hasHistory && (
            <>
              {/* ✅ Header sticky */}
              <div style={workspaceStyles.historySectionHeader}>
                <span style={workspaceStyles.historySectionTitle}>
                  <History size={12} />
                  Histórico ({historyItems.length})
                   <button
                  style={workspaceStyles.clearHistoryBtn}
                  onClick={onClearHistory}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${workspaceColors.danger}15`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Limpar Histórico
                </button>
                </span>
               
              </div>
              {/* Cards do histórico */}
              <div style={workspaceStyles.historyCardsContainer}>
                <ScrollableCardRow workspaceColors={workspaceColors}>
                  {historyItems.map((item) => (
                    <ResultCard
                      key={`history-${item.id}`}
                      result={item}
                      onUseResponse={onUseResponse}
                      onRemove={() => onRemoveFromHistory?.(item.id)}
                      remainingRegenerations={0}
                      isRegenerating={false}
                      isHistoryItem={true}
                    />
                  ))}
                </ScrollableCardRow>
              </div>
            </>
          )}

          {/* Current Results Section */}
          {hasAnyResults && (
            <>
              {isHistoryOpen && hasHistory && (
                <div style={workspaceStyles.currentSectionHeader}>
                  Resultados Atuais
                </div>
              )}
              <ScrollableCardRow workspaceColors={workspaceColors}>
                {/* Translate Results */}
                {translateResults.map((result) => (
                  <ResultCard
                    key={result.id}
                    result={result}
                    onUseResponse={onUseResponse}
                    onRegenerate={() => onRegenerateTranslate?.(result)}
                    remainingRegenerations={getTranslateRemainingRegenerations?.(result.id) ?? MAX_REGENERATIONS}
                    isRegenerating={regeneratingId === result.id}
                    onRemove={() => onRemoveTranslateResult?.(result.id)}
                  />
                ))}

                {/* Generate Results */}
                {generateResults.map((result) => (
                  <ResultCard
                    key={result.id}
                    result={result}
                    onUseResponse={onUseResponse}
                    onRegenerate={() => onRegenerateGenerate?.(result)}
                    remainingRegenerations={getGenerateRemainingRegenerations?.(result.id) ?? MAX_REGENERATIONS}
                    isRegenerating={regeneratingId === result.id}
                    onRemove={() => onRemoveGenerateResult?.(result.id)}
                  />
                ))}

                {/* Custom Results */}
                {customResults.map((result) => (
                  <ResultCard
                    key={result.id}
                    result={result}
                    onUseResponse={onUseResponse}
                    onRegenerate={() => onRegenerateCustom?.(result)}
                    remainingRegenerations={getCustomRemainingRegenerations?.(result.id) ?? MAX_REGENERATIONS}
                    isRegenerating={regeneratingId === result.id}
                    onRemove={() => onRemoveCustomResult?.(result.id)}
                  />
                ))}
              </ScrollableCardRow>
            </>
          )}
        </div>
      </div>

      <style>{`
        .hide-scrollbar-workspace::-webkit-scrollbar {
          display: none;
        }
        
        /* Scrollbar estilizada para área de conteúdo */
        .workspace-content-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .workspace-content-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .workspace-content-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(128, 128, 128, 0.3);
          border-radius: 3px;
        }
        .workspace-content-scroll::-webkit-scrollbar-thumb:hover {
          background-color: rgba(128, 128, 128, 0.5);
        }
      `}</style>
    </>
  );
};

export default AIWorkspace;